import { AnalyticsContext } from '@carvana/eventable-clickstream';
import { isSuccess, type Result } from '@carvana/merch-result';
import { datadogRum } from '@datadog/browser-rum';
import { useCallback, useContext } from 'react';
import { DATADOG_EVENTS } from '~/constants/flp/datadogEvents';
import { reportActionFailure } from '~/utilities/flp/reportClientError';
import { navPerformanceNow } from '~/utilities/navigationTiming';

type ToggleType = 'cosigner' | 'tradein';

type ToggleTrackingConfig = {
  analyticsClickEvent: string;
  datadogClickEvent: string;
  errorEvent: string;
  location: string;
  toggleType: ToggleType;
};

export default function useToggleTracking({
  analyticsClickEvent,
  datadogClickEvent,
  errorEvent,
  location,
  toggleType
}: ToggleTrackingConfig) {
  const { trackEvent } = useContext(AnalyticsContext);

  return useCallback(
    async <T>(toggleAction: boolean, action: () => Promise<Result<T>>) => {
      const toggleStartTime = navPerformanceNow();
      datadogRum.addAction(datadogClickEvent, { toggleAction });
      trackEvent(analyticsClickEvent, { toggleAction });

      try {
        const result = await action();

        if (!isSuccess(result)) {
          reportActionFailure(errorEvent, location, result.errorMessage);

          return;
        }

        datadogRum.addAction(DATADOG_EVENTS.PREQUALIFIED.TOGGLE_COMPLETE, {
          durationMs: navPerformanceNow() - toggleStartTime,
          toggleType
        });
      } catch {
        reportActionFailure(errorEvent, location);
      }
    },
    [
      analyticsClickEvent,
      datadogClickEvent,
      errorEvent,
      location,
      toggleType,
      trackEvent
    ]
  );
}
